<template>
  <div class="container url-planning">
    <div class="url-planning__title-container">
      <div class="url-planning__title-left">
        <div v-if="!isAnyCheckboxSelected" class="url-planning__title">
        </div>
        <button
          v-else-if="!isCurrentUserClientCollaborator"
          class="url-planning__button url-planning__button__asana"
          @click="sendData()"
        >
          Add to Asana
        </button>
      </div>

      <div v-if="isDataFetched" class="url-planning__title-right">
        <base-multi-select
          v-if="urlAttributesOptions.length"
          class="url-planning__title-right_multi-select"
          disableAllOption
          enableRemoveOption
          :options="urlAttributesOptions"
          title="URL Attribute"
          removeOptionLabel="Clear All"
          v-model="filterParams.url_attributes"
          @close="loadFilteredData"
        />
        <base-multi-select
          class="url-planning__title-right_multi-select"
          :options="columnOptions"
          title="Columns"
          v-model="enabledColumns"
        />
        <div class="url-planning__is_optimized">
          <span>Optimized</span>
          <div class="url-planning__is_optimized__options">
            <base-check-box style="margin-right: 15px;"
                            v-model="isOptimized.yes"
                            @change="changeOptimizedStatus($event, 'yes')"
                            label="Yes"/>
            <base-check-box v-model="isOptimized.no"
                            @change="changeOptimizedStatus($event, 'no')"
                            label="No"/>
          </div>
        </div>
        <div class="url-planning__urls">
          <span class="url-planning__count">URLs: {{totalItems}}</span>
          <base-select
            class="url-planning__grid-select"
            :options="paginationOptions"
            alignLeft
            :titleStyle="{ fontSize: '12px', fontWeight: 500 }"
            :headerStyle="{ paddingLeft: '6px' }"
            showTopBorder
            shiftLabelRight
            v-model="pageItemsLimit"
            :selectStyle="{ maxHeight: '24px !important', width: '66px !important' }"
            @change="handleLimitChange"
          />
        </div>
        <button class="url-planning__button" @click="openCreateModal" v-if="!isCurrentUserClientCollaborator">
          Create URL
        </button>
      </div>
    </div>

    <div class="url-planning__pills-wrapper">
      <template
        v-if="isDataFetched && someFiltersOrSortingIsApplied">
        <div
          class="url-planning__header-button-wrapper"
        >
          <button class="url-planning__copy-button"
                  @click.stop="copyFilters">
            <base-material-design-icon icon="copy"/>
            copy filters
          </button>
          <button
            @click.stop="cancelSortingAndFilters"
            class="url-planning__clear-filters">
            <span>clear all filters and sorting</span>
            <span class="url-planning__cross">&times;</span>
          </button>
        </div>
      </template>
      <base-block-chip
          v-for="(chip, index) in filterChips"
          class="url-planning__filter-chip"
          :key="`${chip.value}_${index}`"
          :label="chip.label"
          @remove="removeFilter(chip.value)"
      />
    </div>

    <div class="" v-if="dataMissing">
      <div class="url-planning__no-data">Awaiting Data</div>
    </div>
    <div class="" v-else-if="!isDataFetched">
      <div class="url-planning__loader">
        <base-loader/>
      </div>
    </div>
    <template v-else-if="!enabledColumns.length">
      <div class="no-data-block url-planning__wait-columns">
        Toggle columns for the table view
      </div>
    </template>
    <div v-else class="url-planning__table-wrapper">
      <div
        class="common-table url-planning__table"
        ref="table"
        :class="{ 'url-planning__table_uncollapsed-navbar': !isNavbarCollapsed }"
      >
        <div
          class="common-table__row common-table__header"
          :style="{ width: isScrollAvailable ? 'max-content' : '-webkit-fill-available' }"
        >
          <template v-for="column in urlPlanningTableColumnsConfig">
            <div class="common-table__header-cell"
                 v-if="'integrations' !== column.property"
                 :key="column.property"
                 :style="column.property === 'url' && {
                   minWidth: `${urlColumnWidth + additionalUrlWidth}px`,
                   width: `${urlColumnWidth + additionalUrlWidth}px`
                 }"
                 :class="[`url-planning__${column.property}`]">
              <template v-if="column.property === 'checkboxes' && !isCurrentUserClientCollaborator">
                <base-check-box v-model="isAllCheckboxesSelected"
                                @change="onAllIntegrationsSelectedToggle"/>
              </template>
              <base-multi-select-sortable-header
                v-else-if="'checkboxes' !== column.property"
                class="url-planning__select-header"
                :style="column.property === 'url' && {
                  minWidth: `${urlColumnWidth + additionalUrlWidth}px`,
                  width: `${urlColumnWidth + additionalUrlWidth}px`
                }"
                :column="column"
                :sort-config="sortConfig"
                :predefinedOptions="filterParams[column.property]"
                :predefinedNumberFilter="['msv', 'keywords'].includes(column.property) ? filterParams[column.property] : null"
                :predefinedDateRangeFilter="column.property === 'target_date' ? {start: filterParams['date_from'], end: filterParams['date_to']}: null"
                @sort="onHeaderCellClicked(column, $event)"
                @apply="filterColumn($event, column)"
                @cancel="cancelFilter($event, column)"
                :ref="sortableHeaderRefs[column.property]"
              />
            </div>
          </template>
        </div>
        <template v-if="dataMissing">
          <div class="common-table__row no-data-block">
            Awaiting Data
          </div>
        </template>
        <template v-else>
          <template v-for="(url) in paginatedKeywords">
            <div
              :key="url._id"
              class="common-table__row"
              :style="{ width: isScrollAvailable ? 'max-content' : '-webkit-fill-available' }"
            >
              <div
                v-for="column in urlPlanningTableColumnsConfig"
                :key="column.property"
                class="common-table__cell"
                :style="column.property === 'url' && {
                  minWidth: `${urlColumnWidth + additionalUrlCell}px`,
                  width: `${urlColumnWidth + additionalUrlCell}px`
                }"
                :class="[`url-planning__${column.property}`, `url-planning__row_${column.property}`]"
              >
                <template v-if="column.property === 'checkboxes' && !isCurrentUserClientCollaborator">
                    <base-check-box v-model="selectedUrlIntegrations[url.brief_content._id]"
                                    @change="onIntegrationSelect"/>
                </template>
                <template v-else-if="column.property === 'integrations' && url.is_asana_integrated">
                  <a :href="url.asana_task_url" target="_blank">
                    <img :src="require(`@/assets/integrations_logos/asana-logo.svg`)"/>
                  </a>
                </template>
                <template v-else-if="column.property === 'integrations' && url.is_asana_integration_process">
                  <img class="url-planning__spinning-image"
                       :src="require(`@/assets/integrations_logos/asana-logo-grey.svg`)"/>
                </template>
                <template v-else-if="column.isSelectableValue && column.property === 'url_status'">
                  <base-select
                    class="url-planning__select"
                    :class="{ 'url-planning__select_absolute': (url.objectIsOpened || {})[column.property] }"
                    :options="column.options"
                    border-less
                    alignLeft
                    :disabled="isUrlStatusDisabled(url) || isCurrentUserClientCollaborator"
                    :titleStyle="{'padding-left': '5px'}"
                    showTopBorder
                    shiftLabelRight
                    v-model="url.currentUrlStatus"
                    :selectStyle="{height: '60px!important'}"
                    @toggle="openBaseSelector(url, column, $event)"
                    @change="openModal(url, column)"/>
                </template>
                <template v-else-if="column.isSelectableValue && ['writing_status', 'fk_user_id_writer', 'fk_user_id_strategist'].includes(column.property)">
                  <base-select
                    class="url-planning__select"
                    :class="{ 'url-planning__select_absolute': (url.objectIsOpened || {})[column.property] }"
                    :options="column.options"
                    border-less
                    alignLeft
                    :disabled="url[column.disableKey] || isCurrentUserClientCollaborator"
                    :titleStyle="{
                      'padding-left': '5px',
                       width: column.property ==='writing_status' ? 'auto' : '164px'
                    }"
                    showTopBorder
                    shiftLabelRight
                     v-no-options-message="noContributorsMessage"
                    v-model="url[column.stateValue]"
                    :selectStyle="{height: '60px!important'}"
                    @toggle="openBaseSelector(url, column, $event)"
                    @change="updateWritingStatusOrCBContributor(url, column)"/>
                </template>
                <template v-else-if="column.isLink">
                  <a class="url-planning__link" target="_blank" :href="getColumnData(url, column.property)"
                     v-tooltip.bottom-start="{content: getColumnData(url, column.property), ...tooltipOptions}">
                    <base-truncate-label
                      ref="truncate"
                      :label="getColumnData(url, column.property)"
                      :watchModificator="`[href='${getColumnData(url, column.property)}']`"
                      :watchClass="'url-planning__link'"/>
                  </a>
                </template>
                <template v-else-if="column.property === 'target_date'"
                          :class="{'url-planning__date_wrapper-narrow': !getColumnData(url, column.property)}">
                  <div class="url-planning__date_wrapper"
                       :class="{'url-planning__date_wrapper_disabled': isCurrentUserClientCollaborator}">
                    <v-date-picker class="url-planning__date"
                                   :class="{}"
                                   :ref="url._id"
                                   :popover="{visibility: url.pickerVisibility}"
                                   value=""
                                   @input="selectNewDate($event, url)"
                                   :input-props="{
                                    class: 'hidden',
                                    readonly: true
                                  }">
                    </v-date-picker>
                    <button class="url-planning__date-button" @click="openPicker($event, url)"
                            :class="{'url-planning__date-button_highlighted': url.pickerVisibility === 'visible',
                          'url-planning__date-button_disabled': isCurrentUserClientCollaborator}"
                            v-if="getColumnData(url, column.property)">
                      {{getColumnData(url, column.property) | formattedDate}}
                    </button>
                    <base-material-design-icon icon="calendar"
                                               class="url-planning__date-button url-planning__date-button_icon"
                                               @click="openPicker($event, url)"
                                               :class="{'url-planning__date-button_highlighted': url.pickerVisibility === 'visible',
                                             'url-planning__date-button_disabled': isCurrentUserClientCollaborator}"
                                               v-if="!getColumnData(url, column.property)">
                    </base-material-design-icon>
                  </div>
                  <div>
                    <base-material-design-icon
                      :icon="getIcon(url)"
                      :buttonStyle="{color: '#284aff'}"
                      @click="onArrowIconClick(url)" />
                  </div>
                </template>
                <template v-else-if="['msv', 'keywords'].includes(column.property)">
                  <div class="url-planning__number">
                    {{getColumnData(url, column.property) | commaDividedNumber}}
                  </div>
                </template>
                <template v-else>
                  <div v-if="isLabelActive(url, column.property)" class="url-planning__label-active"
                       @click.prevent.stop="navigateToContentBrief(url)"
                       v-tooltip.bottom-start="{content: 'Click to open content brief', ...tooltipOptions}">
                    {{getColumnData(url, column.property)}}
                  </div>
                  <div v-else class="url-planning__label">
                    {{getColumnData(url, column.property)}}
                  </div>
                </template>
              </div>
            </div>
            <url-keyword-table :url="url"/>
          </template>
        </template>
      </div>
    </div>
    <div class="pagination-wrapper" v-show="hasPagination">
      <base-pagination :limit="pageItemsLimit"
                       :total-items="totalItems"
                       @pageChanged="pageChanged"/>
    </div>
    <div class="url-planning__update-loader" v-if="isDataUpdating">
      <base-loader class="url-planning__update-loader-ring"/>
    </div>
    <modal name="integrations"
           v-bind="integrationModalProps"
           ref="integrations"
    >
      <base-integration-menu :type="type"
                             :currentClientId="currentClientId"
                             :changeAccountLink="integrationLinks[type]"
                             :integrationMode="integrationMode"
                             :connectedServices="metadata.integrated_services"
                             :selectedIntegrations="selectedIntegrations"
                             :isSaveAsDefaults="isSaveAsDefaults"
                             mode="integrate"
                             @sendingData="onIntegrationDataSending"
                             @close="closeIntegrationChange" />
    </modal>
    <modal name="status"
           v-bind="changeStatusModalProps">
      <UrlStatusPopup
        :url="changingStatusData"
        :statuses="urlStatuses"
        @apply="statusApplied"
        @cancel="statusCancel()"/>
    </modal>

    <modal name="create"
           v-bind="changeStatusModalProps">
      <CreateUrlPopup
        ref="create-url"
        @apply="createNewUrl($event)"
        :urls="urlNames"
        @cancel="createNewUrlCancel"/>
    </modal>
  </div>
</template>

<script>
import filters from '@/common/Filters'
import helper from '@/services/Helper'
import { mapActions, mapState } from 'vuex'
import baseModalProps from '@/common/BaseModalProps'
import UrlStatusPopup from './UrlStatusPopup'
import moment from 'moment'
import CreateUrlPopup from './CreateUrlPopup'
import ToasterService from '@/services/toasterService'
import { ALLOWED_BRIEF_STATUSES_TO_NAVIGATE_FROM } from '@/common/AllowedUrlStatuses'
import _ from 'lodash'
import UrlKeywordTable from './UrlKeywordTable'
import BaseSortMixins from '@/common/BaseSortMixins'
import { INTEGRATIONS_MODE } from '@/common/BaseConstants'

const ITEMS_PER_PAGE = 100
const icons = {
  angleRight: 'arrowRight',
  angleDown: 'arrowDown'
}
const EMPTY_URL_FILTERS = {
  brief_status: [],
  section: [],
  url_status: [],
  writing_status: [],
  page_types: [],
  url: null,
  is_optimized: null,
  sort_column: [],
  sort_order: [],
  page_from: 0,
  page_to: ITEMS_PER_PAGE,
  date_from: null,
  date_to: null,
  url_attributes: [],
  fk_user_id_writer: [],
  fk_user_id_strategist: [],
  msv: {
    operator: null,
    value: null
  },
  keywords: {
    operator: null,
    value: null
  }
}
// table config
const DEFAULT_CONFIG = [
  {
    label: '',
    columnLabel: 'Check Box',
    property: 'checkboxes'
  },
  {
    label: 'Target Date',
    property: 'target_date',
    // isDefaultSort: false // this column will be sorted by default
  },
  {
    label: 'URL Status',
    property: 'url_status',
    isSelectableValue: true, // this value could be selected using BaseSelect widget
    isSmartInput: false,
    options: []
  },
  {
    label: 'Brief Status',
    property: 'brief_status',
    isSmartInput: true, // this property activates BaseMultiSelectFilterDropdown in BaseMultiSelectSortableHeader
    options: []
  },
  {
    label: 'Strategist',
    property: 'fk_user_id_strategist',
    stateValue: 'currentCBStrategist',
    disableKey: 'isStrategistSelectDisabled',
    isSelectableValue: true,
    isSmartInput: false,
    options: []
  },
  {
    label: 'Writing Status',
    property: 'writing_status',
    isSelectableValue: true,
    isSmartInput: false,
    disableKey: 'isWritingSelectDisabled',
    stateValue: 'currentWritingStatus',
    options: []
  },
  {
    label: 'Writer',
    property: 'fk_user_id_writer',
    stateValue: 'currentCBWriter',
    disableKey: 'isWriterSelectDisabled',
    isSelectableValue: true,
    isSmartInput: false,
    options: []
  },
  {
    label: 'Page Types',
    property: 'page_types',
    // isSelectableValue: true, // this value could be selected using BaseSelect widget
    isSmartInput: false,
    options: []
  },
  {
    label: 'Section',
    property: 'section',
    isSmartInput: true,
    options: []
  },
  {
    label: 'URL',
    property: 'url',
    isLink: true // external link
  },
  {
    label: '# KW',
    property: 'keywords',
    isNumber: true
  },
  {
    label: 'MSV',
    property: 'msv',
    isNumber: true
  },
  {
    label: '',
    columnLabel: 'Asana Icon',
    property: 'integrations'
  }
]
const MIN_URL_COLUMN_WIDTH = 360
const DEFAULT_SELECTED_COLUMNS = [
  'checkboxes',
  'target_date',
  'url_status',
  'brief_status',
  'page_types',
  'url',
  'keywords',
  'msv',
  'integrations'
]
/**
 * Url planning component
 * Pay attention: on this page we have backend sort/pagination/filter functionality
 */
export default {
  name: 'UrlPlanning',
  components: { UrlKeywordTable, UrlStatusPopup, CreateUrlPopup },
  mixins: [BaseSortMixins],
  // filters received form url
  props: ['filters', 'integrationStatus'],
  created () {
    // callback to close manually opened date picker
    const clickHandler = function (event) {
      const hasSomeParentTheClass = (element, classname) => {
        if (element && element.classList && element.classList.contains(classname)) return true
        return element.parentNode && hasSomeParentTheClass(element.parentNode, classname)
      }
      if (!this.isDatePickerOpened || hasSomeParentTheClass(event.target, 'classname')) {
        return
      }
      const classList = Array.from(_.get(event, 'target.classList', []))
      const insideDatePicker = classList.some(className => className.startsWith('vc-'))
      if (insideDatePicker) {
        return
      }
      this.sortedUrls && this.sortedUrls.forEach(url => {
        url.pickerVisibility = 'hidden'
      })
      this.isDatePickerOpened = false
      this.openedUrlDatePickerId = null
    }
    this.handler = clickHandler.bind(this)
    document.addEventListener('click', this.handler)
    window.addEventListener('resize', this.throttledCalculateUrlColumnWidth)

    try {
      const enabledColumns = JSON.parse(localStorage.getItem('enabledColumns'))
      if (Array.isArray(enabledColumns)) {
        this.enabledColumns = enabledColumns
      }
    } catch {
    }
  },
  beforeDestroy () {
    document.removeEventListener('click', this.handler)
    window.removeEventListener('resize', this.throttledCalculateUrlColumnWidth)
  },
  mounted () {
    const integrationStatus = this.integrationStatus
    if (integrationStatus && integrationStatus.length) {
      try {
        const statusData = JSON.parse(integrationStatus)
        this.tempClientId = statusData.client_id
        this.type = statusData.type
        this.isSaveAsDefaults = true
        this.isIntegrationProcess = true

        const selectedIntegrations = JSON.parse(localStorage.getItem('selectedIntegrations'))
        localStorage.removeItem('selectedIntegrations')
        const selectedUrlIntegrations = {}
        selectedIntegrations.forEach(i => { selectedUrlIntegrations[i] = true })
        this.selectedUrlIntegrations = selectedUrlIntegrations

        Object.assign(this, statusData.data)
        this.$modal.show('integrations')
      } catch (e) {
        console.log(e)
      } finally {
        // remove integration status from url
        this.$router.push(this.$route.path)
      }
    }
    if (this.isStaticView) {
      const footer = document.querySelector('footer')
      footer.hidden = true
    }
    const unwatch = this.$watch('isDataFetched', async () => {
      await this.$nextTick()
      this.throttledCalculateUrlColumnWidth()
      unwatch()
    })
  },
  data () {
    return {
      isDataFetched: false,
      isDataUpdating: false,
      isDatePickerOpened: false,
      isAllCheckboxesSelected: false,
      openedUrlDatePickerId: null,
      isIntegrationProcess: false,
      integrationMode: INTEGRATIONS_MODE.ASANA_ADD,
      // uncomment if new actions will be adde
      // integrationModes: INTEGRATIONS_MODE,
      selectedUrlIntegrations: {},
      enabledColumns: [...DEFAULT_SELECTED_COLUMNS],
      filteredUrls: [],
      sortConfig: [],
      // urls shown in the table
      sortedUrls: [],
      handler: null,
      urlNames: [],
      throttledCalculateUrlColumnWidth: _.throttle(this.calculateUrlColumnWidth, 500),
      enabledUrlAttributes: [],
      isSaveAsDefaults: false,
      urlColumnWidth: MIN_URL_COLUMN_WIDTH,
      type: 'asana',
      integrationLinks: null,
      tempClientId: null,
      pageItemsLimit: ITEMS_PER_PAGE,
      changingStatusData: {},
      totalItems: 0,
      // flag notifies that filters received form url query was applied
      isQueryFiltersApplied: false,
      filterParams: _.cloneDeep(EMPTY_URL_FILTERS),
      isOptimized: {
        yes: false,
        no: false
      },
      icon: icons.angleRight,
      tooltipOptions: {
        delay: { show: 100, hide: 500 },
        classes: 'rfx-tooltip'
      },
      sortableHeaderRefs: {
        'target_date': 'target_date',
        'url_status': 'url_status',
        'brief_status': 'brief_status',
        'writing_status': 'writing_status',
        'fk_user_id_writer': 'fk_user_id_writer',
        'fk_user_id_strategist': 'fk_user_id_strategist',
        'page_types': 'page_types',
        'section': 'section',
        'url': 'url',
        'keywords': 'keywords',
        'msv': 'msv'
      },
      paginationOptions: [
        {
          label: '100',
          value: 100
        },
        {
          label: '250',
          value: 250
        },
        {
          label: '500',
          value: 500
        }
      ],
      noContributorsMessage: 'This client doesn\'t contain any contributors',
    }
  },
  computed: {
    ...mapState(['selectedClient', 'urlStatuses', 'isNavbarCollapsed']),
    ...mapState('rankZoneCommon', ['pageTypes', 'writingStatuses', 'clientContributors']),
    ...mapState('urlPlanning', ['urls', 'metadata', 'settings', 'keywords']),
    dataMissing () {
      return this.isDataFetched && !this.sortedUrls
    },
    isScrollAvailable () {
      return this.urlColumnWidth === MIN_URL_COLUMN_WIDTH
    },
    urlMinActualDiff () {
      return this.urlColumnWidth - MIN_URL_COLUMN_WIDTH
    },
    additionalUrlCell () {
      return this.enabledColumns.includes('integrations') &&
      !this.enabledColumns.includes('msv') &&
      !this.enabledColumns.includes('keywords')
        ? this.urlMinActualDiff <= 50 ? -this.urlMinActualDiff : -50
        : this.urlMinActualDiff ? 0 : -2
    },
    additionalUrlWidth () {
      return this.enabledColumns.includes('integrations') &&
      !this.enabledColumns.includes('msv') &&
      !this.enabledColumns.includes('keywords')
        ? !this.urlMinActualDiff ? 52 : this.urlMinActualDiff < 50 ? 50 - this.urlMinActualDiff : 0
        : this.enabledColumns.includes('integrations') &&
        this.enabledColumns.includes('msv') &&
        this.urlMinActualDiff >= 50
          ? 50
          : 0
    },
    hasPagination () {
      return (this.isDataFetched && !this.isDataUpdating) && this.totalItems > this.pageItemsLimit && this.enabledColumns.length
    },
    urlPlanningTableColumnsConfig () {
      const defaultConfig = _.cloneDeep(DEFAULT_CONFIG)

      // set options
      const settings = _.cloneDeep(this.settings)
      const sections = settings.sections.map(helper.convertListToOptions)
      defaultConfig.find(item => item.property === 'section').options = sections

      const briefStatuses = settings.brief_statuses.map(helper.convertListToOptions)
      defaultConfig.find(item => item.property === 'brief_status').options = briefStatuses

      const writingStatuses = this.writingStatuses.map(helper.convertListToOptions)
      defaultConfig.find(item => item.property === 'writing_status').options = writingStatuses

      const clientContributors = this.clientContributors.map(helper.convertListToOptions)
      defaultConfig.find(item => item.property === 'fk_user_id_writer').options = clientContributors
      defaultConfig.find(item => item.property === 'fk_user_id_strategist').options = clientContributors

      const pageTypes = this.pageTypes.map(helper.convertListToOptions)
      defaultConfig.find(item => item.property === 'page_types').options = pageTypes

      defaultConfig.find(item => item.property === 'url_status').options = this.urlStatuses
        .map(item => {
          return {
            label: item.label,
            value: item._id
          }
        })

      // remove checkboxes is user is client colloborator
      if (this.isCurrentUserClientCollaborator) {
        defaultConfig.shift()
      }
      return defaultConfig.filter(i => this.enabledColumns.includes(i.property))
    },
    urlAttributesOptions () {
      return _.cloneDeep(this.settings.url_attributes).map(i => ({ label: i.label, value: i._id }))
    },
    columnOptions () {
      const tableConfig = _.cloneDeep(DEFAULT_CONFIG)
      return tableConfig.map(i => ({ label: i.columnLabel || i.label, value: i.property }))
    },
    isAnyCheckboxSelected () {
      return Object.values(this.selectedUrlIntegrations).includes(true)
    },
    selectedIntegrations () {
      return Object.entries(this.selectedUrlIntegrations).reduce((arr, [key, value]) => {
        if (value) {
          arr.push(key)
        }
        return arr
      }, [])
    },
    paginatedKeywords () {
      let sortedUrls = _.cloneDeep(this.sortedUrls)
      // filter urls for different integrations modes
      // if (this.integrationMode && this.integrationMode.toLowerCase().includes('add')) {
      //   sortedUrls = sortedUrls.filter(item => !item.is_asana_integrated)
      // } else if (this.integrationMode) {
      //   sortedUrls = sortedUrls.filter(item => item.is_asana_integrated)
      // }
      const { page_from = 0, page_to = 100 } = this.filterParams
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.totalItems = sortedUrls.length
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.filteredUrls = sortedUrls
      return sortedUrls.slice(page_from, page_to)
    },
    integrationModalProps () {
      const shareModalProps = {
        width: '610px'
      }
      return { ...baseModalProps, ...shareModalProps }
    },
    changeStatusModalProps: function () {
      const changeStatusModalProps = {
        width: 690
      }
      return { ...baseModalProps, ...changeStatusModalProps }
    },
    currentClientId () {
      return this.clientId || this.tempClientId
    },
    /**
     * return true if some filters/sort config are present
     * */
    someFiltersOrSortingIsApplied () {
      const { brief_status, section, url_status, writing_status, fk_user_id_writer, fk_user_id_strategist, url, msv, page_types, keywords, sort_column, sort_order, date_to, date_from } = this.filterParams
      let somethingIsApplied = false
      if (brief_status.length > 0 || page_types.length > 0 || section.length > 0 || url_status.length > 0 || writing_status.length > 0 ||  fk_user_id_writer.length > 0 || fk_user_id_strategist.length > 0 || sort_column.length > 0 || sort_order.length > 0) {
        somethingIsApplied = true
      }

      const { operator: msv_operator, value: msv_value } = msv
      const { operator: keywords_operator, value: keywords_value } = keywords

      if (!somethingIsApplied && (page_types || url || msv_value || keywords_value || date_to || date_from || msv_operator || keywords_operator)) {
        somethingIsApplied = true
      }

      return somethingIsApplied
    },
    filterChips () {
      const filterParams = _.cloneDeep(this.filterParams)
      const mapSelectedOptionsToChips = (label, optionValues, arr, options, key) => {
        for (let optionValue of optionValues) {
          arr.push({
            label: `${label} equal to ${options.find(item => item.value === optionValue)?.label}`,
            value: key + '|' + optionValue
          })
        }
      }
      return Object.keys(filterParams).reduce((arr, key) => {
        if (
          (Array.isArray(filterParams[key]) && !filterParams[key].length) ||
          (['msv', 'keywords'].includes(key) && !filterParams[key].operator) ||
          [undefined, null].includes(filterParams[key])
        ) {
          return arr
        }
        const options = this.urlPlanningTableColumnsConfig.find(i => i.property === key)?.options
        switch (key) {
          case 'date_from':
            let label = ''
            if (filterParams.date_from === filterParams.date_to) {
              label = `target date equal to ${filterParams.date_from}`
            } else {
              label = `target date from ${filterParams.date_from} to ${filterParams.date_to}`
            }
            arr.push({
              label,
              value: 'date'
            })
            break
          case 'brief_status':
            mapSelectedOptionsToChips('brief status', filterParams.brief_status, arr, options, key)
            break
          case 'section':
            mapSelectedOptionsToChips('section', filterParams.section, arr, options, key)
            break
          case 'url_status':
            mapSelectedOptionsToChips('url status', filterParams.url_status, arr, options, key)
            break
          case 'writing_status':
            mapSelectedOptionsToChips('writing status', filterParams.writing_status, arr, options, key)
            break
          case 'page_types':
            mapSelectedOptionsToChips('page type', filterParams.page_types, arr, options, key)
            break
          case 'url':
            arr.push({
              label: `url contains ${filterParams.url}`,
              value: key
            })
            break
          case 'url_attributes':
            mapSelectedOptionsToChips(
              'url attribute',
              filterParams.url_attributes,
              arr,
              this.urlAttributesOptions,
              key
            )
            break
          case 'fk_user_id_writer':
            mapSelectedOptionsToChips('writer', filterParams.fk_user_id_writer, arr, options, key)
            break
          case 'fk_user_id_strategist':
            mapSelectedOptionsToChips('strategist', filterParams.fk_user_id_strategist, arr, options, key)
            break
          case 'keywords':
          case 'msv':
            const getOperatorLabel = (operator) => {
              switch (operator) {
                case 'equal':
                  return 'equal to'
                case 'ne':
                  return 'not equal'
                case 'gt':
                  return 'greater than'
                case 'lt':
                  return 'less than'
                case 'lte':
                  return 'less than/equal to'
                case 'gte':
                  return 'greater than/equal to'
              }
            }
            arr.push({
              label: `${key === 'keywords' ? key + ' count' : key} ${getOperatorLabel(filterParams[key].operator)} ${filterParams[key].value}`,
              value: key
            })
        }
        return arr
      }, [])
    }
  },
  watch: {
    selectedClient (client, previousClient) {
      if (!previousClient || !client) {
        // this way we try to avoid twice call loadPageData() from here and from urlStatus watcher by start app form UrlStatus page
        return
      }
      this.isDataFetched = false
      this.reset()
    },
    enabledColumns: {
      handler (v) {
        localStorage.setItem('enabledColumns', JSON.stringify(v))
        this.throttledCalculateUrlColumnWidth()
      },
      deep: true
    },
    isNavbarCollapsed () {
      if (this.$refs && this.$refs.truncate && this.$refs.truncate.length) {
        this.$refs.truncate.forEach(i => { i.resizeListener() })
      }
    },
    paginatedKeywords (item) {
      const isNeedUpdate = item.some(i => i.is_asana_integration_process)
      if (isNeedUpdate) {
        setTimeout(() => {
          this.getUrlPlannerData({
            clientId: this.clientId,
            filters: this.filterParams,
            clearData: false
          })
        }, 3000)
      }
    },
    urls () {
      this.urlNames = []
      this.urls.forEach(item => {
        this.urlNames.push(item.url)
        // we use currentUrlStatus and previousUrlStatus to save url status by changing it while corresponding modal is opened
        // currentUrlStatus variable bound to the url status BaseSelect
        item.isWritingSelectDisabled = false
        item.currentWritingStatus = item['writing_status']._id
        item.previousWritingStatus = item['writing_status']._id
        item.currentCBWriter = item['fk_user_id_writer']._id
        item.previousCBWriter = item['fk_user_id_writer']._id
        item.currentCBStrategist = item['fk_user_id_strategist']._id
        item.previousCBStrategist = item['fk_user_id_strategist']._id
        item.currentUrlStatus = item['url_status']._id
        item.previousUrlStatus = item['url_status']._id
        item.pickerVisibility = this.openedUrlDatePickerId === item._id ? 'visible' : 'hidden'
        item.isExpanded = false
        item.children = null
      })
      this.sortedUrls = _.cloneDeep(this.urls)
    },
    isAllCheckboxesSelected: {
      handler () {
        this.updateIntegrationLinks()
      },
      immediate: true
    },
    clientId: {
      handler () {
        if (!this.isIntegrationProcess) {
          this.selectedUrlIntegrations = {}
          this.isAllCheckboxesSelected = false
        }
        this.updateIntegrationLinks()
        this.updateUrlStatusesOptions()
      },
      immediate: true
    },
    isOptimized: {
      handler () {
        this.updateIntegrationLinks()
      },
      deep: true,
      immediate: true
    },
    filterParams: {
      handler (v) {
        this.updateIntegrationLinks()
      },
      deep: true,
      immediate: true
    },
    selectedUrlIntegrations: {
      handler () {
        this.updateIntegrationLinks()
      },
      deep: true
    },
    // handle integration mode change (for multiple actions)
    // integrationMode: {
    //   handler (value, oldValue) {
    //     if (!value) {
    //       this.selectedUrlIntegrations = {}
    //     } else if (value.includes('add') || (!value.includes('add') && oldValue && oldValue.includes('add'))) {
    //       this.$nextTick(() => {
    //         const selectedUrlIntegrations = {}
    //         if (this.isAllCheckboxesSelected) {
    //           const filteredUrls = _.cloneDeep(this.filteredUrls)
    //           filteredUrls.forEach(item => {
    //             selectedUrlIntegrations[item.brief_content._id] = true
    //           })
    //         }
    //         this.selectedUrlIntegrations = selectedUrlIntegrations
    //       })
    //     }
    //   }
    // },
    keywords: {
      handler (keywords) {
        Object.keys(keywords)
          .filter(key => keywords[key])
          .forEach(key => {
            const url = this.sortedUrls.find(item => item._id === key)
            if (url) {
              this.$set(url, 'children', keywords[key])
              this.removeKeywordsByIrlId({ urlId: url._id })
            }
          })
      },
      deep: true
    }
  },
  filters: {
    commaDividedNumber: filters.commaDividedNumber,
    formattedDate: filters.formattedDate
  },
  methods: {
    ...mapActions('urlPlanning', [
      'getUrlPlannerData', 'resetData', 'changeStatus', 'createUrl', 'getUrlPlannerSettings',
      'getKeywordsByUrlIdData', 'removeKeywordsByIrlId', 'changeWritingStatus'
    ]),
    ...mapActions('briefContent', ['changeContentBrief']),
    /**
     * method restores filterParams from url query filters and notifies about possibility to load page data
     * @returns {boolean} true - if loadPageData is allowed. Returns false in case when we need to change client
     * in this case all logic will be repeated again after changing client
     */
    setupFilterParams () {
      // this.filters - filters received form url query as props
      if (!this.filters || this.isQueryFiltersApplied) {
        this.filterParams = { ...this.filterParams, ..._.cloneDeep(EMPTY_URL_FILTERS) }
        return true
      }
      const filters = JSON.parse(this.filters)
      const clientId = filters.client_id
      // we need to change client, return false
      if (clientId !== this.clientId) {
        if (!this.setNewClient(clientId, true)) {
          // user does not allowed to see this client, filters will be ignored
          console.warn(this.$route.name + ' client with id ', clientId, 'does not connected to current user')
          this.isQueryFiltersApplied = true
          this.filterParams = { ...this.filterParams, ..._.cloneDeep(EMPTY_URL_FILTERS) }
          return true
        }
        return false
      }
      // there is exactly the same client selected
      this.filterParams = _.cloneDeep({ ...this.filterParams, ..._.cloneDeep(EMPTY_URL_FILTERS), ...filters })
      if (filters.sort_column && filters.sort_column.length > 0) {
        filters.sort_column.forEach((columnName, index) => {
          const propertyName = columnName
          const sortDirection = filters.sort_order[index]
          const valuePreFormatter = null
          this.sortConfig.push({ propertyName, sortDirection, valuePreFormatter })
        })
      }
      this.isQueryFiltersApplied = true
      return true
    },
    async calculateUrlColumnWidth () {
      if (!this.enabledColumns.includes('url')) {
        return
      }
      await this.$nextTick()
      const table = this.$refs.table
      if (table) {
        const urlColumnWidth = Array.from(table.firstChild.childNodes).reduce(
          (elementsWidth, el) => {
            if (el.classList && !el.classList.contains('url-planning__url')) {
              elementsWidth -= el.offsetWidth
            }
            return elementsWidth
          },
          this.enabledColumns.includes('msv') && this.enabledColumns.includes('integrations')
            ? table.clientWidth - 50
            : table.clientWidth
        )
        this.urlColumnWidth = urlColumnWidth > MIN_URL_COLUMN_WIDTH ? urlColumnWidth : MIN_URL_COLUMN_WIDTH
      }
    },
    /**
     * Returns true if url is active
     * Enable possibility to open content brief for current URL
     */
    isLabelActive: function (url, column) {
      if (['section', 'page_types'].includes(column)) {
        return false
      }
      const briefStatusName = url.brief_status.name ? url.brief_status.name.toLowerCase() : ''

      return ALLOWED_BRIEF_STATUSES_TO_NAVIGATE_FROM.indexOf(briefStatusName) !== -1 || this.isCurrentUserAgent
    },
    /**
     * url with status Published are not editable
     * */
    isUrlStatusDisabled (url) {
      return !!(url.url_status && url.url_status.name.toLowerCase() === 'published')
    },
    /**
     * changes number of paginated items in teh list
     * */
    handleLimitChange: function (value) {
      this.pageItemsLimit = value
      this.filterParams = { ...this.filterParams, page_from: 0, page_to: value }
      // this.loadFilteredData()
    },
    /**
     * clear all filters/sorting
     * */
    cancelSortingAndFilters: function () {
      const emptyFilters = _.cloneDeep(EMPTY_URL_FILTERS)
      this.filterParams = { ...this.filterParams, ...emptyFilters, page_to: this.pageItemsLimit }
      Object.keys(this.sortableHeaderRefs).filter(i => this.enabledColumns.includes(i))
        .forEach(ref => {
          const [element] = this.$refs[ref]
          element.$refs[element.childRef].cancel(true)
        })
      this.isOptimized = {
        yes: false,
        no: false
      }
      this.sortConfig = []
      !this.isDataUpdating && this.loadFilteredData()
    },
    onUrlAttributesChange (urlAttributes) {
      this.filterParams.url_attributes = urlAttributes
      this.loadFilteredData()
    },
    updateUrlStatusesOptions () {
      if (!this.urlStatuses || !this.urlStatuses.length) {
        return
      }

      this.filterParams.allowed_statuses = this.urlStatuses
        .map(item => {
          return item._id
        })
      if (this.setupFilterParams()) {
        this.loadPageData()
      }
    },
    openBaseSelector (url, column, status = true) {
      const sortedUrls = _.cloneDeep(this.sortedUrls)
      url.objectIsOpened = {
        ...url.objectIsOpened,
        [column.property]: status
      }
      sortedUrls[sortedUrls.findIndex(i => i._id === url._id)] = url
      this.sortedUrls = sortedUrls

      if (status) {
        this.$nextTick(() => {
          const parentElements = this.$el.querySelectorAll(`.url-planning__row_${column.property}`)
          let element = parentElements[0].querySelector('.url-planning__select:not(.url-planning__select_absolute)')
          if (!element) {
            element = parentElements[1].querySelector('.url-planning__select:not(.url-planning__select_absolute)')
          }
          const { left } = element.getBoundingClientRect()
          const targetElement = this.$el.querySelector('.url-planning__select.url-planning__select_absolute')
          targetElement.style.left = left - 130 + 'px'
        })
      } else {
        const targetElement = this.$el.querySelector('.url-planning__select.url-planning__select_absolute')
        targetElement.style.left = ''
      }
    },
    /**
     * Shows us content brief of current URL
     */
    navigateToContentBrief (item) {
      const briefContentId = item.brief_content._id
      const briefStatusName = item.brief_status.name ? item.brief_status.name.toLowerCase() : ''
      if (ALLOWED_BRIEF_STATUSES_TO_NAVIGATE_FROM.indexOf(briefStatusName) === -1 && !this.isCurrentUserAgent) {
        return
      }
      const tab = item.url_status.name.toLowerCase() === 'published' ? 'progress' : 'strategy'
      const routeData = this.$router.resolve({ path: `/content-briefs/${briefContentId}/${tab}` })
      window.open(routeData.href, '_blank')
    },
    changeOptimizedStatus (status, statusName) {
      const isAllDisabled = !Object.values(this.isOptimized).includes(true)
      if (isAllDisabled) {
        this.filterColumn(null, { property: 'is_optimized' })
      } else {
        this.isOptimized = {
          [statusName]: status,
          [statusName === 'yes' ? 'no' : 'yes']: !status
        }
        this.filterColumn(statusName === 'yes' ? 1 : 0, { property: 'is_optimized' })
      }
    },
    /**
     * reset data after change client
     * */
    reset () {
      this.urlNames = []
      this.sortConfig = []
      this.totalItems = 0
      this.setupFilterParams()
      this.loadPageData()
    },
    /**
     * callback for header checkbox that manages all table checkboxes state
     */
    onAllIntegrationsSelectedToggle (status) {
      const selectedUrlIntegrations = {}
      this.paginatedKeywords.forEach(item => {
        selectedUrlIntegrations[item.brief_content._id] = status
      })
      this.selectedUrlIntegrations = Object.assign({}, this.selectedUrlIntegrations, selectedUrlIntegrations)
    },
    /**
     * callback for checkbox that manages integrations
     */
    onIntegrationSelect (status) {
      const selectedUrlIntegrations = Object.values(this.selectedUrlIntegrations)
      if (status && selectedUrlIntegrations.length === this.filteredUrls.length && !selectedUrlIntegrations.includes(false)) {
        this.isAllCheckboxesSelected = true
      } else {
        this.isAllCheckboxesSelected = false
      }
    },
    sendData () {
      this.$modal.show('integrations')
    },
    onIntegrationDataSending () {
      this.isDataUpdating = true
      // this.$modal.hide('integrations')
      this.$refs.integrations.$el.style.marginTop = '-200vh'
    },
    /**
     * Change sort direction for column (asc/desc)
     */
    onHeaderCellClicked (cell, direction, declineCall) {
      if (direction === 'clear') {
        this.sortConfig.shift()
        return
      }
      if (this.sortConfig.length) {
        this.sortConfig[0].propertyName = cell.property
        this.sortConfig[0].sortDirection = direction
      } else if (cell) {
        this.sortConfig = [{
          sortDirection: direction,
          propertyName: cell.property
        }]
      }
      this.filterParams = {
        ...this.filterParams,
        sort_column: this.sortConfig.map(item => item.propertyName),
        sort_order: this.sortConfig.map(item => item.sortDirection)
      }
      !declineCall && this.loadFilteredData()
    },
    getNewSortConfigItem (cell, direction) {
      return {
        sortDirection: direction,
        propertyName: cell.property,
        valuePreFormatter: this.getSortPreformatting(cell)
      }
    },
    /**
     * Select filters for column
     * then {@link #loadFilteredData()} load data with filters
     */
    filterColumn (event, column) {
      //  Works when we change filters for Date column
      if (column.property === 'target_date') {
        this.filterParams.date_from = event.date_from
        this.filterParams.date_to = event.date_to
        //  Works when we change filters for Keywords or Msv column's
      } else if (['keywords', 'msv'].includes(column.property)) {
        const { value, operator } = event
        // this.filterParams[column.property] = { value, operator }
        this.$set(this.filterParams, column.property, { value, operator })
      } else {
        this.filterParams[column.property] = event
      }
      this.filterParams.page_from = 0
      this.filterParams.page_to = this.pageItemsLimit
      this.loadFilteredData()
    },
    /**
     * clear specific filter
     * */
    cancelFilter (event, column) {
      if (column.property === 'target_date') {
        this.filterParams.date_from = null
        this.filterParams.date_to = null
      } else {
        this.filterParams[column.property] = null
      }
      this.loadFilteredData()
    },
    getColumnData (url, property) {
      if (_.isObject(url[property])) {
        return _.get(url, [property, 'name'])
      }
      return url[property]
    },
    async loadFilteredData () {
      this.isDataUpdating = true
      await this.getUrlPlannerData({
        clientId: this.clientId,
        filters: this.filterParams
      })
      this.isDataUpdating = false
    },
    loadPageData: async function () {
      this.isDataFetched = false
      this.resetData()
      // set default sort config for page
      const defaultSortedColumn = this.urlPlanningTableColumnsConfig.find(item => item.isDefaultSort)
      this.onHeaderCellClicked(defaultSortedColumn, helper.DIRECTION_DESC, true)
      await Promise.all([
        // this call returns client-specific settings for BaseMultiSelectSortableHeader
        this.getUrlPlannerSettings({ clientId: this.clientId, filters: this.filterParams }),
        this.getUrlPlannerData({
          clientId: this.clientId,
          filters: this.filterParams,
          clearData: true
        })])
      this.isDataFetched = true
    },
    /**
     * Change page of URLs (pagination changes)
     */
    pageChanged ({ from, to }) {
      // let isPageChanged = false
      // if (this.filterParams.page_from !== from || this.filterParams.page_to !== to) {
      //   isPageChanged = true
      // }
      this.filterParams.page_from = from
      this.filterParams.page_to = to
      this.$nextTick(() => {
        const selectedUrlIntegrations = _.cloneDeep(this.selectedUrlIntegrations)
        const isSelected = this.paginatedKeywords.some(item => selectedUrlIntegrations[item.brief_content._id])
        this.isAllCheckboxesSelected = isSelected
      })
      // isPageChanged && this.loadFilteredData()
    },
    /**
     * Open {@link ./UrlStatusPopup}
     */
    openModal (url) {
      this.changingStatusData = url
      this.$modal.show('status')
    },
    async closeIntegrationChange () {
      this.isIntegrationProcess = false
      this.selectedUrlIntegrations = {}
      this.isAllCheckboxesSelected = false
      await this.loadFilteredData()
      this.isDataUpdating = false
      this.$modal.hide('integrations')
    },
    async updateWritingStatusOrCBContributor (url, column) {
      url[column.disableKey] = true
      let writingAssignedStatusId = null
      try {
        if (column.property === 'writing_status') {
          await this.changeWritingStatus({
            url_id: url._id,
            writing_status: url.currentWritingStatus
          })
        } else {
          writingAssignedStatusId = this.writingStatuses.find(status => status.label.toLowerCase() === 'assigned')._id
          url.currentWritingStatus = writingAssignedStatusId
          await Promise.all([
            this.changeContentBrief({
              clientId: this.clientId,
              briefId: url.brief_content._id,
              [column.property]: column.property === 'fk_user_id_strategist'
                ? url.currentCBStrategist
                : url.currentCBWriter
            }),
            this.changeWritingStatus({
              url_id: url._id,
              writing_status: writingAssignedStatusId,
              successMessage: false
            })
          ])
        }
        switch (column.property) {
          case 'writing_status':
            url.previousWritingStatus = url.currentWritingStatus
            break
          case 'fk_user_id_strategist':
            url.previousCBStrategist = url.currentCBStrategist
            break
          case 'fk_user_id_writer':
            url.previousCBWriter = url.currentCBWriter
            url.previousWritingStatus = writingAssignedStatusId
            break
        }
      } catch (error) {
        switch (column.property) {
          case 'writing_status':
            url.currentWritingStatus = url.previousWritingStatus
            break
          case 'fk_user_id_strategist':
            url.currentCBStrategist = url.previousCBStrategist
            break
          case 'fk_user_id_writer':
            url.currentCBWriter = url.previousCBWriter
            break
        }
      } finally {
        url[column.disableKey] = false
        this.openBaseSelector(url, column, false)
      }
    },
    /**
     * Async update URL status on backend
     */
    async statusApplied ({ description, date }) {
      this.$modal.hide('status')
      this.isDataUpdating = true
      if (date !== 'clear') {
        if (moment(date).isValid()) {
          date = moment(date).format('YYYY-MM-DD')
        } else {
          date = null
        }
      }
      try {
        await this.changeStatus({
          clientId: this.clientId,
          urlId: this.changingStatusData._id,
          statusId: this.changingStatusData.currentUrlStatus,
          description,
          date
        })
        await this.loadFilteredData()
        this.isDataUpdating = false
      } catch (error) {
        this.isDataUpdating = false
      }
    },
    statusCancel () {
      // return previous status
      this.changingStatusData.currentUrlStatus = this.changingStatusData.previousUrlStatus
      this.$modal.hide('status')
    },
    removeFilter (chipValue) {
      const [key, value = null] = chipValue.split('|')
      if (Array.isArray(this.filterParams[key])) {
        this.filterParams[key] = this.filterParams[key].filter(i => i !== value)
      } else if (key === 'date_from') {
        this.filterParams.date_from = null
        this.filterParams.date_to = null
      } else {
        this.filterParams[key] = _.cloneDeep(EMPTY_URL_FILTERS[key])
      }

      this.loadFilteredData()
    },
    updateIntegrationLinks () {
      const redirectUrl = `${window.location.origin}/integrations/asana/oauth`
      const state = JSON.stringify({
        'client_id': this.clientId,
        'redirect_url': redirectUrl,
        'current_route': this.$route.fullPath,
        'data': {
          'isAllCheckboxesSelected': this.isAllCheckboxesSelected,
          'isOptimized': this.isOptimized,
          'filterParams': this.filterParams
          // 'selectedUrlIntegrations': this.selectedUrlIntegrations
        }
      })
      this.integrationLinks = {
        'asana': `https://app.asana.com/-/oauth_authorize?client_id=1170381332056778&redirect_uri=${redirectUrl}&response_type=code&state=${state}`
      }
    },
    /**
     * Open data picker for current URL
     */
    openPicker (event, url) {
      this.openedUrlDatePickerId = url._id
      this.changingStatusData = url
      this.sortedUrls = [...this.sortedUrls].map(item => {
        item.pickerVisibility = 'hidden'
        return item
      })
      let handler = () => {
        url = this.sortedUrls.find(i => i._id === url._id)
        url.pickerVisibility = 'visible'
        this.isDatePickerOpened = true
        this.$nextTick(this.addClearButton)
      }
      handler = handler.bind(this)
      //  Timeout on show data picker
      setTimeout(handler, 300)
    },
    /**
     * Update date for current URL,
     * @param event - selected date in data picker
     */
    selectNewDate (event, url) {
      this.changingStatusData = url
      this.statusApplied({ date: event })
    },
    clearDateListener () {
      this.statusApplied({ date: 'clear' })
    },
    /**
     * Hide {@link ./CreateUrlPopup}
     * Create new URL
     */
    createNewUrl: async function (urlData) {
      // this.$modal.hide('create')
      this.isDataUpdating = true
      if (moment(urlData.date).isValid()) {
        urlData.date = moment(urlData.date).format('YYYY-MM-DD')
      } else {
        urlData.date = null
      }
      try {
        await this.createUrl({ clientId: this.clientId, ...urlData })
        this.$modal.hide('create')
        await Promise.all([
          this.getUrlPlannerSettings({ clientId: this.clientId, filters: this.filterParams }),
          this.loadFilteredData()
        ])
      } catch (e) {
        this.$refs['create-url'].showError(e[0])
      } finally {
        this.isDataUpdating = false
      }
    },
    createNewUrlCancel () {
      this.$modal.hide('create')
    },
    /**
     * Shows {@link ./CreateUrlPopup}
     */
    openCreateModal () {
      this.$modal.show('create')
    },
    /**
     * Shows us {@link UrlKeywordTable}
     */
    onArrowIconClick (url) {
      url.isExpanded = !url.isExpanded
      if (!url.children) {
        this.getKeywordsByUrlIdData({ clientId: this.clientId, filters: { filterParams: this.filterParams, url_id: url._id } })
      }
    },
    /**
     * icon to mark expanded/collapsed row state
     */
    getIcon (url) {
      return url.isExpanded ? icons.angleDown : icons.angleRight
    },
    copyFilters () {
      let path = `${window.location.origin}${window.location.pathname}#${this.$route.path}?filters=`
      const copiedFilters = { client_id: this.clientId }
      Object.entries(this.filterParams).forEach(([key, value]) => {
        if (_.isArray(value) && value.length > 0) {
          copiedFilters[key] = _.cloneDeep(value)
        }
        if (_.isInteger(value) || (_.isString(value) && Boolean(value))) {
          copiedFilters[key] = value
        }
        if (['msv', 'keywords'].includes(key)) {
          if (value.value && value.operator) {
            copiedFilters[key] = _.cloneDeep(value)
          }
        }
      })
      path += JSON.stringify(copiedFilters)
      path = encodeURI(path)
      this.$copyText(path)
        .then(
          e => {
            ToasterService.showToast(ToasterService.INFO_TYPE, 'Filters copied to clipboard')
          },
          err => {
            ToasterService.showToast(ToasterService.ERROR_TYPE, 'Can not copy filters')
          }
        )
    }
  }
}
</script>

<style scoped lang="scss">

  .common-table {
    border-radius: 4px;
    overflow: visible;

    &__header {
      border-top-right-radius: 4px;
      border-top-left-radius: 4px;
      position: relative;
    }

    &__header-cell {
      color: $rfx-dark-march;
      background-color: rgba(1,22,39,.1);
      font-weight: bold;
      padding: 0;
      height: 60px;
      display: flex;
      align-items: center;
      cursor: pointer;

      &:first-child {
        padding-left: 25px;
      }
    }

    &__cell,
    &__header-cell {
      &:last-child {

        &.url-planning__msv,
        &.url-planning__keywords {
          min-width: 160px !important;
          flex-basis: 160px !important;;

          .url-planning__select-header {
            //width: 100% !important;
            flex-basis: 160px !important;;
            width: 160px !important;;
          }
        }
      }
    }

    &__cell {
      height: 60px;
      padding: 0;
      display: flex;
      align-items: center;
      font-size: 14px;
      border-bottom-color: $rfx-light-steel;

      &:first-child {
        padding-left: 25px;
      }

      &:last-child {

        &.url-planning__row_keywords,
        &.url-planning__row_msv {
          flex: 1 1 110px;
        }
      }
    }

    &__row {
      width: max-content;
    }
  }

  .url-planning {
    position: relative;
    min-height: 200vh!important;

    &__filter-chip {
      margin-right: 8px;
      float: left;
      margin-bottom: 14px;
    }

    &__keyword-header{
      background-color: rgba(1, 22, 39, 0.1);
    }

    &__cross {
      font-size: 21px;
      margin-left: 5px;
    }

    &__title {
      font-size: 36px;
      line-height: 30px;
      letter-spacing: -0.41px;
      font-weight: 300;
      margin-bottom: 16px;
      height: 48px;
    }

    &__title-right {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      flex: 1 1 50%;
      height: 48px;

      &_multi-select {
        align-self: flex-end;
        height: 48px;

        &::v-deep .base-multi-select__header {
          height: 46px;

          .base-multi-select__title {
            color: $rfx-dark-march;
            font-weight: normal;
            font-size: 14px;
            letter-spacing: -.16px;
            text-transform: none;
          }

          .base-multi-select__arrow {
            top: 19px;
          }
        }
      }
    }

    &__title-right > *:not(:last-child) {
      margin-right: 30px;
    }

    &__title-left {
    }

    &__title-left > *:not(:last-child) {
      margin-right: 30px;
    }

    &__pills {
      &-wrapper {
        display: inline-block;
      }
    }

    &__header-button-wrapper {
      border-radius: 15px;
      border: solid 1px $rfx-light-steel;
      background: white;
      height: 28px;
      display: flex;
      padding: 0 10px;
      width: fit-content;
      float: left;
      margin-right: 24px;
      margin-bottom: 14px;
    }

    &__wait-columns {
      background: white;
      height: 300px;
      border-bottom: none;
    }

    &__copy-button {
      flex: 1 1 50%;
      height: 100%;
      border: none;
      outline: none;
      color: $rfx-dark-march;
      white-space: nowrap;
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 90px;
      background-color: #fff;
      font-size: 11px;

      &:hover {
        opacity: 0.5;
      }
    }

    &__clear-filters {
      @extend .url-planning__copy-button;
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-left: 1px solid $rfx-light-steel;
      width: auto;

      span:first-child {
        font-size: 11px;
      }
    }

    &__grid-select {
      flex-basis: 100px;
    }

    &__title-container {
      padding-top: 14px;
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
    }

    &__loader {
      display: flex;
      width: 100%;
      justify-content: center;
      align-items: center;
      height: calc(100vh - 530px);
    }

    &__table {
      display: flex;
      flex-direction: column;
      overflow-x: scroll;
      overflow-y: hidden;
      border-left: 1px solid $rfx-light-steel;
      border-right: 1px solid $rfx-light-steel;

      &-wrapper {
        position: relative;
      }

      &_uncollapsed-navbar {
        .common-table__header-cell {
          &:last-child.url-planning__keywords {
            flex-basis: 10.2% !important;
          }
        }
      }
    }

    &__update-loader {
      position: absolute;
      display: flex;
      width: 100%;
      height: 100%;
      justify-content: center;
      background-color: white;
      top: 0;
      opacity: 0.5;
      z-index: 10000;
    }

    &__update-loader-ring {
      margin-top: 200px;
    }

    &__date-button {
      outline: none;
      border: none;
      text-align: initial;
      font-size: 14px;
      background-color: #fff;
      color: $rfx-dark-march;
      padding-left: 13px;

      &_disabled{
        pointer-events: none;
      }

      &_highlighted {
        color: $rfx-bright-blue;
      }

      &:hover {
        color: $rfx-bright-blue;
      }

      &_icon {
        align-self: auto;
      }
    }

    &__date {
      height: 0;
      max-width: 110px;

      &_disabled{
        pointer-events: none;
      }
    }

    &__urls {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: space-between;
      height: 100%;

      &::v-deep .select__arrow {
        flex-basis: 30px;
      }
    }

    &__is_optimized {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: space-between;
      height: 100%;

      span {
        color: $rfx-silver;
        font-size: 12px;
        letter-spacing: -0.14px;
      }

      &__options {
        display: flex;
        justify-content: space-between;
        width: 100%;

        &::v-deep .checkbox__label {
          color: $rfx-dark-march;
        }
      }
    }

    &__spinning-image {
      height: 16px;
      position: absolute;
      top: 50%;
      left: 15%;
      margin: -8px 0 0 -8px;
      animation:spin 4s linear infinite;
  }
  @keyframes spin { 100% { -webkit-transform: rotate(360deg); transform:rotate(360deg); } }

    &__number {
      padding-left: 15px;
    }

    &__date_wrapper {
      display: flex;
      flex-direction: column;
      cursor: pointer;

      &_disabled{
        pointer-events: none;
      }
    }

    &__date_wrapper-narrow {
      width: 14px;
    }

    &__label-active {
      padding-left: 15px;
      overflow: hidden;
      @include ellipsis;
      cursor: pointer;
      color: $rfx-bright-blue;
    }

    &__label-active:hover {
      color: $rfx-bright-blue;
    }

    &__label {
      padding-left: 15px;
      overflow: hidden;
      @include ellipsis;
    }

    &__select-header {
      position: absolute !important;
      height: 60px !important;
    }

    &__select {
      flex-basis: 100%;

      &_absolute {
        position: absolute !important;
      }

      &::v-deep .select__list {
        min-width: fit-content;
      }
    }

    &__no-data {
      display: flex;
      height: 300px;
      background-color: #fff;
      border-radius: $rfx-default-border-radius;
      align-items: center;
      justify-content: center;
      font-size: 27px;
      font-weight: 600;
      color: $rfx-medium-grey;
      flex: 1 1;
    }

    &__url {

      &.common-table__header-cell {
        flex: 0;
      }
    }

    &__target_date,
    &__url_status,
    &__writing_status,
    &__fk_user_id_writer,
    &__fk_user_id_strategist,
    &__brief_status,
    &__page_types,
    &__keywords,
    &__msv,
    &__section {
      flex: 0 0 150px;
      color: $rfx-light-march;
      min-width: 150px;

      .url-planning__select-header,
      .url-planning__select_absolute {
        width: 150px;

        &::v-deep .select__list {
          min-width: 150px;
        }
      }
    }

    &__checkboxes {
      min-width: 45px;
      flex: 0 0 45px;
    }

    &__integrations {
      position: relative;
      flex: 0 0 25px;
      min-width: 25px;
      padding-right: 25px;
      img {
        width: 16px;
      }
    }

    &__writing_status,
    &__fk_user_id_strategist,
    &__fk_user_id_writer,
    &__url_status {
      min-width: 145px;
      flex-basis: 145px;
      color: $rfx-dark-march;
      font-weight: 500;

      .url-planning__select-header,
      .url-planning__select_absolute {
        width: 145px;

        &::v-deep .select__list {
          min-width: 145px;
        }
      }
    }

    &__fk_user_id_writer,
    &__fk_user_id_strategist {
      min-width: 180px;
      flex: 0 0 180px;

      .url-planning__select-header,
      .url-planning__select_absolute {
        width: 180px;

        &::v-deep .select__list {
          min-width: 180px;
        }
      }
    }

    &__keywords {
      min-width: 110px;
      flex-basis: 110px;

      &.common-table__header-cell {

        &:hover {
          background-color: $rfx-light-grey;
        }
      }

      .url-planning__select-header {
        width: 110px;

        &::v-deep .multi-select__header {
          &:hover {
            background-color: transparent;
          }
        }
      }
    }

    &__msv {
      min-width: 110px;
      flex-basis: 110px;
      /*padding-right: 25px;*/

      &.common-table__header-cell {
        min-width: 110px;
        flex-basis: 110px;

        &:hover {
          background-color: $rfx-light-grey;
        }

        .url-planning__select-header {
          width: 110px;

          &::v-deep .multi-select__header {
            &:hover {
              background-color: transparent;
            }
          }
        }
      }
    }

    &__section {
      min-width: 150px;
      flex-basis: 150px;
    }

    &__brief_status {
      flex-basis: 145px;
      min-width: 145px;
    }

    &__target_date {
      min-width: 145px;
      flex-basis: 145px;
      /*padding-left: 25px;*/
    }

    &__link {
      flex: 1 1;
      padding-left: 15px;
      @include ellipsis;
      text-decoration: none;
      color: $rfx-bright-blue;
    }

    &__row {
      &_section,
      &_brief_status,
      &_url {
        overflow: hidden;
      }
    }

    &__button {
      width: 136px;
      padding: 0 20px;
      height: 48px;
      border-radius: 4px;
      font-size: 10px;
      font-weight: 900;
      text-transform: uppercase;
      border: none;
      outline: none;
      cursor: pointer;
      background-color: $rfx-medium-march;
      color: #fff;

      &__asana {
        margin-bottom: 16px;
      }
    }

    &__count {
      color: $rfx-silver;
      font-size: 12px;
      letter-spacing: -0.14px;
      text-decoration: none;
      width: 75px;
      white-space: nowrap;
    }
  }

  .pagination-wrapper {
    display: flex;
    height: 60px;
    margin-top: 10px;
    justify-content: center;
    align-items: center;
    border: 1px solid $rfx-light-steel;
    border-radius: 4px;
  }

  .no-data-block {
    border-bottom: 1px solid $rfx-light-steel;
  }

  .common-table {
    &__header-cell {
      color: $rfx-dark-march;
      font-weight: bold;
    }
  }

</style>
